import React from 'react'
import { differenceInMonths } from 'date-fns'
import { observer } from 'mobx-react-lite'

import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'

import type { DateRange } from '@mui/x-date-pickers-pro'

import ConnectionsDrawer from '../accounts/components/ConnectionsDrawer'
import {
  ANALYTICS_EVENTS,
  EmptyState,
  Loader,
  useEvent,
  useGlobalCopyStore,
  useGlobalUiStore,
  useNetWorthStore,
  useScreenSize,
  useWidgetLoadTimer,
  WidgetContainer,
} from '../common'
import { WidgetProps } from '../core'

import NetWorthChart from './components/NetWorthChart'
import NetWorthList from './components/NetWorthList'
import NetWorthSummary from './components/NetWorthSummary'

export const NetWorthWidget: React.FC<WidgetProps> = observer(({ onBackClick, onMenuClick }) => {
  const { isInitialized } = useGlobalUiStore()
  const { accounts: accountsCopy, net_worth: copy } = useGlobalCopyStore()
  const {
    hoveredData,
    isNetWorthDataLoaded,
    loadNetWorthData,
    netWorthChartData,
    selectedData,
    setTimeframe,
  } = useNetWorthStore()
  const { isMobile } = useScreenSize()
  const { onEvent } = useEvent()

  const [showConnectionsWidget, setShowConnectionsWidget] = React.useState(false)

  useWidgetLoadTimer({
    widgetName: 'NetWorthWidget',
    isLoaded: isNetWorthDataLoaded,
  })

  React.useEffect(() => {
    if (!isNetWorthDataLoaded) {
      loadNetWorthData().finally()
    }
    onEvent(ANALYTICS_EVENTS.NET_WORTH_VIEW)
  }, [])

  const handleRangeChanged = async (range: DateRange<Date>) => {
    const startDate = range[0]
    const endDate = range[1]

    if (startDate && endDate) {
      setTimeframe(differenceInMonths(endDate, startDate))
    }
  }

  const handleTimeframeClick = (months: number) => {
    onEvent(ANALYTICS_EVENTS.NET_WORTH_WIDGET_CLICK_TIMEFRAME, { time_period: months + 'M' })
  }

  const handleManageConnections = () => {
    setShowConnectionsWidget(true)
    onEvent(ANALYTICS_EVENTS.ACCOUNTS_CLICK_CONNECT)
  }

  if (!isInitialized || !isNetWorthDataLoaded) return <Loader />

  const showEmptyState = netWorthChartData.length === 0

  return (
    <WidgetContainer
      calendarActions={{
        onRangeChanged: handleRangeChanged,
        onTimeframeClick: handleTimeframeClick,
      }}
      dateRangeVariant={isMobile ? 'timeframetabs' : 'timeframebuttons'}
      onAccountsFilterClick={() => onEvent(ANALYTICS_EVENTS.NET_WORTH_WIDGET_CLICK_FILTER)}
      onBackClick={onBackClick}
      onMenuClick={onMenuClick}
      title={copy.net_worth}
    >
      {!showEmptyState ? (
        <>
          {selectedData && (
            <NetWorthSummary hoveredData={hoveredData} selectedData={selectedData} />
          )}
          <Stack
            alignItems={isMobile ? 'center' : 'flex-start'}
            direction={isMobile ? 'column' : 'row'}
            spacing={isMobile ? 0 : 48}
          >
            <NetWorthChart />
            <NetWorthList />
          </Stack>
        </>
      ) : (
        <Box sx={{ justifyItems: 'center' }}>
          <EmptyState
            header={copy.empty_widget_header}
            icon={'elevation'}
            onClick={handleManageConnections}
            primaryButton={copy.empty_widget_button}
            subText={copy.empty_widget_description}
            sx={{
              width: { sm: 432 },
              '.buttons-footer': {
                width: 167,
              },
            }}
          />
        </Box>
      )}

      <ConnectionsDrawer
        onClose={() => setShowConnectionsWidget(false)}
        showConnectionsWidget={showConnectionsWidget}
        title={accountsCopy.manage_connections}
      />
    </WidgetContainer>
  )
})
